
  import { Component, Vue } from 'vue-property-decorator'
  import { mapGetters } from 'vuex'

  import CartWidget from '@/components/cart/CartWidget.vue'
  import DemoCartWidget from '@/components/cart/DemoCartWidget.vue'
  import MobileDemoCartWidget from '@/components/cart/MobileDemoCartWidget.vue'
  import BundleMobileCartButton from '@/components/elements/BundleMobileCartButton.vue'
  import MobileCartButton from '@/components/elements/MobileCartButton.vue'
  import Price from '@/components/elements/Price.vue'
  import { openModal } from '@/modals/helpers/api'

  @Component({
    components: {
      BundleMobileCartButton,
      CartWidget,
      DemoCartWidget,
      MobileDemoCartWidget,
      MobileCartButton,
      Price,
    },
    computed: {
      ...mapGetters({
        cartItems: 'Cart/cartItems',
      }),
    },
  })
  export default class BundleDemoRoute extends Vue {
    created() {
      // Mock the cart data as early as possible
      // This ensures CartWidget can read from the store when it initializes
      this.mockCartData()
    }

    openBundleCart() {
      openModal({
        name: 'mobile-bundle-cart',
        component: MobileDemoCartWidget,
        title: 'Bundle Cart',
      })
    }

    mockCartData() {
      // Create a minimal apiResponse structure that the Cart store expects
      // The cartItems getter derives from apiResponse, so we need to provide this structure
      const devExpiry = new Date().getTime() + 15 * 60 * 1000 // 15 minutes from now
      const expiresIn = 15 * 60 // 15 minutes in seconds

      const mockApiResponse = {
        cart: {
          _count: 1,
          _data: [
            {
              id: 'demo-cart-id',
              cart_number: 'DEMO123',
              identity_id: 'e80c5c71-f119-11e7-92ca-06b36975a689',
              portal_id: 'd3e8e658-a074-4956-a941-270039b75ac4',
              state: 'pending',
              expires_at: new Date(devExpiry).toISOString(),
              expires_in: expiresIn,
              invalid: false,
              managed: false,
              name: '',
              channel_id: 'web',
              seller_id: null,
              additional_info: null,
            },
          ],
          _total: 1,
        },
        ticket: {
          _count: 1,
          _data: [
            {
              id: 'demo-ticket-id',
              cart_id: 'demo-cart-id',
              event_template_id: 'e2e-upsell-template-id',
              event_session_id: null, // No session for always-available events
              ticket_type_id: 'adult-ticket-type-id',
              ticket_group_id: 'general-admission-group-id',
              ticket_order_id: null,
              quantity: 2,
              state: 'pending',
              price: '32.00', // $32 per ticket
              valid_from: new Date().toISOString(),
              valid_to: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString(), // 1 year from now
            },
          ],
          _total: 1,
        },
        event_template: {
          _count: 1,
          _data: [
            {
              id: 'e2e-upsell-template-id',
              name: "E2E Upsell, always-available, DON'T CHANGE",
              portal_id: 'd3e8e658-a074-4956-a941-270039b75ac4',
              event_type: 'always_available',
              category: 'Public',
              _rank: 1000,
              venue_id: 'demo-venue-id',
            },
          ],
          _total: 1,
        },
        event_session: {
          _count: 0,
          _data: [],
          _total: 0,
        },
        ticket_type: {
          _count: 1,
          _data: [
            {
              id: 'adult-ticket-type-id',
              name: 'Adult',
              ticket_group_id: 'general-admission-group-id',
            },
          ],
          _total: 1,
        },
        ticket_group: {
          _count: 1,
          _data: [
            {
              id: 'general-admission-group-id',
              name: 'General Admission',
              event_template_id: 'e2e-upsell-template-id',
            },
          ],
          _total: 1,
        },
        cart_fees: {
          _count: 1,
          _data: [
            {
              cart_id: 'demo-cart-id',
              currency_code: 'USD',
              gateway_id: 'stripe',
              grand_totals: {
                total_before_cartmod_price: '64.00',
                total_after_fees_price: '64.00',
                total_before_fees_price: '64.00',
                total_cartmod_amount: '0',
                total_fee_fixed_outside: '0',
                total_fee_percent_outside: '0',
                total_fee_fixed_inside: '0',
                total_fee_percent_inside: '0',
                total_outstanding: '64.00',
                total_payments: '0',
              },
            },
          ],
          _total: 1,
        },
        cartmod: {
          _count: 0,
          _data: [],
          _total: 0,
        },
        identity: {
          _count: 1,
          _data: [
            {
              id: 'e80c5c71-f119-11e7-92ca-06b36975a689',
              email: 'web-app@ticketure.com',
            },
          ],
          _total: 1,
        },
        meta: {
          _count: 2,
          _data: [
            {
              metakey: 'image_profile',
              resource: 'event_template',
              resource_id: 'e2e-upsell-template-id',
              value: 'https://images.unsplash.com/photo-1441974231531-c6227db76b6e?w=128&h=102&fit=crop',
            },
            {
              metakey: 'image_profile_alt_text',
              resource: 'event_template',
              resource_id: 'e2e-upsell-template-id',
              value: "E2E Upsell, always-available, DON'T CHANGE",
            },
          ],
          _total: 2,
        },
        venue: {
          _count: 1,
          _data: [
            {
              id: 'demo-venue-id',
              name: 'Demo Venue',
              portal_id: 'd3e8e658-a074-4956-a941-270039b75ac4',
              timezone: 'Pacific/Auckland', // Must match portal timezone
            },
          ],
          _total: 1,
        },
        config: {
          _count: 0,
          _data: [],
          _total: 0,
        },
        scheduled_payment: {
          _count: 0,
          _data: [],
          _total: 0,
        },
        gateway_audit: {
          _count: 0,
          _data: [],
          _total: 0,
        },
        seller: {
          _count: 0,
          _data: [],
          _total: 0,
        },
      }

      // Set the apiResponse in the store - this will trigger the cartItems getter
      this.$store.commit('Cart/apiResponse', mockApiResponse)
      this.$store.commit('Cart/localExpiry', devExpiry)
    }

    // Bundle cart with Total Experience bundle
    get bundleCartItems() {
      return [
        {
          id: 'total-experience-bundle',
          name: 'Total Experience',
          image: {
            url: 'https://images.unsplash.com/photo-1441974231531-c6227db76b6e?w=300&h=200&fit=crop&crop=center',
            alt: 'Total Experience Bundle',
          },
          isAnchor: true,
          isMembershipEvent: false,
          isBundle: true,
          bundleItems: [
            {
              name: 'General Admission',
              price: 50.0, // $50.00 (2 adults × $25.00)
              time: null,
              sessionTime: 'Sun 16 Nov @ 10:00 AM',
              types: [
                {
                  type: {
                    id: 'adult-ticket',
                    name: 'Adult',
                  },
                  group: {
                    name: 'General Admission',
                  },
                  ticketCount: 2,
                  totalPrice: 50.0,
                  name: 'Adult',
                },
              ],
            },
            {
              name: 'Bugs Lab Exhibition',
              price: 20.0, // $20.00 (2 adults × $10.00)
              time: 'Anytime',
              sessionTime: 'Sun 16 Nov @ 2:00 PM',
              types: [
                {
                  type: {
                    id: 'adult-ticket',
                    name: 'Adult',
                  },
                  group: {
                    name: 'Bugs Lab Exhibition',
                  },
                  ticketCount: 2,
                  totalPrice: 20.0,
                  name: 'Adult',
                },
              ],
            },
            {
              name: 'Space: A New Frontier 3D Film',
              price: 16.0, // $16.00 (2 adults × $8.00)
              time: '10:00 AM',
              sessionTime: 'Sun 16 Nov @ 4:00 PM',
              types: [
                {
                  type: {
                    id: 'adult-ticket',
                    name: 'Adult',
                  },
                  group: {
                    name: 'Space: A New Frontier 3D Film',
                  },
                  ticketCount: 2,
                  totalPrice: 16.0,
                  name: 'Adult',
                },
              ],
            },
          ],
          types: [
            {
              type: {
                id: 'bundle-ticket',
                name: 'Total Experience',
              },
              group: {
                name: 'Bundle',
              },
              ticketCount: 1,
              totalPrice: 86.0, // $86.00 ($50 + $20 + $16)
              name: 'Total Experience',
            },
          ],
        },
      ]
    }
  }
